import { z } from 'zod';
import { CHANNELS, ESCALATION_KINDS, INSTALLMENT_STATUSES, PAYMENT_METHODS } from '../domain';
import { isoDateSchema } from './referentials';

// Opérations de la phase 2 (spec 05.7, 07, 08) : paiements manuels, actions de masse,
// escalades, droits des assurés, intégrations.

const millimes = z.number().int().positive().max(1_000_000_000_000);

// --- Paiement hors plateforme (BA-035) ---

export const manualPaymentSchema = z.object({
  amount: millimes,
  method: z.enum(
    PAYMENT_METHODS.filter((m) => m !== 'kashy') as ['agency', 'bank', 'check', 'other'],
  ),
  paidAt: isoDateSchema,
  reference: z.string().trim().max(64).nullable().optional(),
  /** Reçu Beta Assur envoyé à l'assuré (le reçu papier de l'agence prévaut sinon). */
  issueReceipt: z.boolean().optional(),
});

export const paymentResultSchema = z.object({
  paymentId: z.string(),
  duplicate: z.boolean(),
  installmentStatus: z.enum(INSTALLMENT_STATUSES).nullable(),
  overpaid: z.number().int(),
});

// --- Actions de masse (BA-040) ---

export const BULK_ACTIONS = [
  'start',
  'pause',
  'resume',
  'stop',
  'change_scenario',
  'mark_paid',
] as const;

export const bulkActionSchema = z
  .object({
    ids: z.array(z.string().min(1).max(64)).min(1).max(500),
    action: z.enum(BULK_ACTIONS),
    scenarioId: z.string().optional(),
    method: z.enum(['agency', 'bank', 'check', 'other']).optional(),
    /** Récapitulatif sans effet (spec 08 : nombre d'éléments avant confirmation). */
    dryRun: z.boolean().default(false),
  })
  .refine((b) => b.action !== 'change_scenario' || !!b.scenarioId, {
    message: 'Scénario requis',
    path: ['scenarioId'],
  })
  .refine((b) => b.action !== 'mark_paid' || !!b.method, {
    message: 'Mode de paiement requis',
    path: ['method'],
  });

export const bulkResultSchema = z.object({
  action: z.enum(BULK_ACTIONS),
  dryRun: z.boolean(),
  requested: z.number().int(),
  applicable: z.number().int(),
  applied: z.number().int(),
  skipped: z.record(z.string(), z.number().int()),
});

// --- Escalades (BA-039) ---

/** Issue d'un appel à l'assuré (BA-092). */
export const CALL_OUTCOMES = [
  'reached',
  'promise',
  'refused',
  'no_answer',
  'wrong_number',
  'already_paid',
] as const;
export type CallOutcome = (typeof CALL_OUTCOMES)[number];

export const ESCALATION_ACTIONS = [
  'take',
  'note',
  'call',
  'mark_paid',
  'grant_delay',
  'cancel_contract',
  'close',
  'assign',
] as const;

export const escalationQuerySchema = z.object({
  status: z.enum(['open', 'in_progress', 'closed']).optional(),
  kind: z.enum(ESCALATION_KINDS).optional(),
  /** `me` : assignées à moi ; `team` : non assignées ; absent : toutes. */
  assigned: z.enum(['me', 'team']).optional(),
  /** `today` : appels à passer aujourd'hui (BA-092), rappels échus compris. */
  calls: z.enum(['today']).optional(),
  sort: z.enum(['age', 'amount']).default('age'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const escalationRowSchema = z.object({
  id: z.string(),
  kind: z.enum(ESCALATION_KINDS),
  reason: z.string(),
  status: z.enum(['open', 'in_progress', 'closed']),
  openedAt: z.string(),
  closedAt: z.string().nullable(),
  assignedUserId: z.string().nullable(),
  lastCallOutcome: z.enum(CALL_OUTCOMES).nullable(),
  callAttempts: z.number().int(),
  callBackAt: z.string().nullable(),
  installment: z
    .object({
      id: z.string(),
      dueDate: z.string(),
      amountDue: z.number().int(),
      amountPaid: z.number().int(),
      status: z.enum(INSTALLMENT_STATUSES),
      policyNumber: z.string(),
      policyholderName: z.string(),
    })
    .nullable(),
});

export const escalationListSchema = z.object({
  items: z.array(escalationRowSchema),
  counts: z.object({ mine: z.number().int(), team: z.number().int(), open: z.number().int() }),
});

export const escalationDetailSchema = escalationRowSchema.extend({
  detail: z.record(z.string(), z.unknown()).nullable(),
  /** Numéro de l'assuré pour l'appel (BA-092) ; nul sans numéro joignable. */
  phone: z.string().nullable(),
  /** Paiements enregistrés sur l'échéance (contexte de l'appel). */
  payments: z.array(
    z.object({
      amount: z.number().int(),
      method: z.string(),
      status: z.string(),
      confirmedAt: z.string().nullable(),
    }),
  ),
  resolution: z.string().nullable(),
  inbound: z
    .object({
      body: z.string(),
      channel: z.enum(CHANNELS),
      receivedAt: z.string(),
      /** Seconde opinion du modèle de langage (BA-060), à vérifier par le gestionnaire. */
      suggestedIntent: z.enum(['paid_elsewhere', 'stop', 'later', 'question', 'other']).nullable(),
      suggestionConfidence: z.number().int().nullable(),
    })
    .nullable(),
  notes: z.array(
    z.object({
      id: z.string(),
      action: z.string(),
      body: z.string().nullable(),
      authorUserId: z.string().nullable(),
      at: z.string(),
    }),
  ),
  messages: z.array(
    z.object({
      id: z.string(),
      channel: z.enum(CHANNELS),
      status: z.string(),
      sentAt: z.string().nullable(),
      direction: z.enum(['out', 'in']),
    }),
  ),
});

export const escalationActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('take') }),
  z.object({ action: z.literal('note'), note: z.string().trim().min(1).max(2000) }),
  z
    .object({
      action: z.literal('call'),
      note: z.string().trim().max(2000).optional(),
      /** Issue de l'appel ; absente, l'appel est seulement noté. */
      outcome: z.enum(CALL_OUTCOMES).optional(),
      /** Promesse de paiement : relances suspendues jusqu'à cette date. */
      promiseDate: isoDateSchema.optional(),
      /** Rappel programmé (instant ISO) ; par défaut le prochain jour ouvré sans réponse. */
      callBackAt: z.string().datetime({ offset: true }).optional(),
    })
    .refine((v) => v.outcome !== 'promise' || v.promiseDate, {
      message: 'Date de paiement promise requise',
      path: ['promiseDate'],
    }),
  z.object({
    action: z.literal('mark_paid'),
    method: z.enum(['agency', 'bank', 'check', 'other']),
    amount: millimes.optional(),
    paidAt: isoDateSchema,
    reference: z.string().trim().max(64).optional(),
    note: z.string().trim().max(2000).optional(),
  }),
  z.object({
    action: z.literal('grant_delay'),
    days: z.number().int().min(1).max(90),
    note: z.string().trim().max(2000).optional(),
  }),
  z.object({ action: z.literal('cancel_contract'), reason: z.string().trim().min(1).max(500) }),
  z.object({ action: z.literal('close'), resolution: z.string().trim().min(1).max(500) }),
  z.object({ action: z.literal('assign'), userId: z.string().nullable() }),
]);

// --- Droits et contact de l'assuré (BA-042, BA-043) ---

export const optOutUpdateSchema = z.object({
  /** Canaux refusés ; vide : opposition levée (consentement recueilli par le gestionnaire). */
  channels: z.array(z.enum(CHANNELS)).max(3),
  reason: z.string().trim().max(500).optional(),
});

export const phoneVerificationConfirmSchema = z.object({ code: z.string().regex(/^\d{4,8}$/) });

// --- Intégrations (lecture seule pour le tenant, ADR-024) ---

const integration = z.object({
  configured: z.boolean(),
  accountId: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export const integrationStatusSchema = z.object({
  connect: integration,
  kashy: integration,
  sendingSuspended: z.object({ at: z.string(), reason: z.string() }).nullable(),
});

// --- Journal de caisse (BA-073) ---

export const cashJournalQuerySchema = z.object({
  date: isoDateSchema,
  /** Auteur (`user:<id>`) ; ignoré pour un agent, qui ne voit que ses saisies. */
  recordedBy: z.string().max(80).optional(),
});
export const cashJournalSchema = z.object({
  date: z.string(),
  items: z.array(
    z.object({
      id: z.string(),
      at: z.string(),
      amount: z.number().int(),
      method: z.enum(PAYMENT_METHODS),
      reference: z.string().nullable(),
      policyNumber: z.string().nullable(),
      installmentId: z.string().nullable(),
      recordedBy: z.string().nullable(),
    }),
  ),
  totals: z.array(
    z.object({
      method: z.enum(PAYMENT_METHODS),
      count: z.number().int(),
      amount: z.number().int(),
    }),
  ),
  total: z.number().int(),
});
